import { useAuth } from '../context/AuthContext.js';

/**
 * 036 US1/US2 — persistent banner shown on every screen while an admin is
 * impersonating a user. Names the target and offers a one-click exit that ends
 * the session and restores the admin's own identity. Uses the app's primary
 * yellow (#FFD800) on dark for an unmistakable, always-visible bar.
 *
 * 078: `sticky`, not `fixed`. As `fixed` it was out of flow and covered the top strip of every
 * screen; that had been harmless only because nothing interactive was ever placed there. When 078
 * mounted the notification bell in the shell, the bell's whole 40px box landed inside the banner's,
 * and `elementFromPoint` at the bell's centre returned this component's "Exit impersonation"
 * button — so the bell was unreachable while impersonating, and a tap aimed at it ended the
 * session. Exactly the case FR-007 exists for. Found in a browser at Gate A with 604 tests green,
 * because jsdom has no layout.
 *
 * `sticky top-0` (129: now on a shared wrapper in `main.tsx`, not this element — see that file's
 * own comment) keeps FR-003/SC-003 whole — persistent, unmistakable, pinned while scrolling — and
 * gives the bar flow space at its own height. Offsetting the bell by a constant instead could not
 * have been correct: this bar is 40px at 1440px wide and 76px at 390px, and grows further with a
 * longer target name. `inset-x-0` is dropped because a block-level sticky element already spans its
 * container, and left/right offsets on one distort that width.
 */
export function ImpersonationBanner() {
  const { isImpersonating, impersonation, exitImpersonation } = useAuth();
  if (!isImpersonating || !impersonation) return null;

  return (
    <div
      role="alert"
      class="flex items-center justify-between gap-3 bg-[#FFD800] px-4 py-2 text-sm font-medium text-black shadow-md"
    >
      <span>
        Impersonating <strong>{impersonation.target.name}</strong> — actions are recorded.
      </span>
      <button
        type="button"
        onClick={() => void exitImpersonation()}
        class="shrink-0 rounded-full bg-black px-3 py-1 text-xs font-semibold text-[#FFD800] hover:opacity-90"
      >
        Exit impersonation
      </button>
    </div>
  );
}
